import i18n from "@/i18n"

// Resolve the current i18n language to a BCP-47 tag Intl can use.
// i18n.language can be missing or set to a short code ("fr") — we map
// to canonical tags so number/date formatting stays consistent.
function currentLocale(): string {
  const lng = i18n.language || ""
  if (lng.toLowerCase().startsWith("fr")) return "fr-FR"
  return "en-US"
}

// Round to N decimals, half-away-from-zero. Returns a plain Number —
// not a string — so callers can keep doing math without re-parsing.
// Used as the single rounding boundary: storage layer rounds once with
// this, display layer rounds once with this, no double-rounding in between.
export function roundTo(value: number, decimals = 2): number {
  if (!Number.isFinite(value)) return 0
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

export function formatNumber(
  value: number | null | undefined,
  fractionDigits = 0,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "0"
  return new Intl.NumberFormat(currentLocale(), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)
}

// formatKg / formatMt are the canonical "weight to string" helpers.
// They both round to 2 decimals — what we display equals what we store.
// Use these instead of bare .toFixed() so the locale's decimal
// separator (',' for fr, '.' for en) flows through automatically.
export function formatKg(
  kg: number | null | undefined,
  fractionDigits = 2,
): string {
  return formatNumber(kg ?? 0, fractionDigits)
}

export function formatMt(
  kg: number | null | undefined,
  fractionDigits = 2,
): string {
  return formatNumber(kgToMt(kg ?? 0), fractionDigits)
}

export function formatDate(value: string | null | undefined, locale?: string): string {
  if (!value) return ""
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleDateString(locale ?? currentLocale(), {
    year: "numeric",
    month: "short",
    day: "2-digit",
  })
}

export function kgToMt(kg: number | null | undefined): number {
  if (kg === null || kg === undefined) return 0
  return kg / 1000
}

export function mtToKg(mt: number | null | undefined): number {
  if (mt === null || mt === undefined) return 0
  return mt * 1000
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export function percent(numerator: number, denominator: number): number {
  if (!denominator) return 0
  return clamp((numerator / denominator) * 100, 0, 100)
}
